import * as Sentry from "@sentry/react";

const SUPPRESSION_INTERVAL_MS = 300_000; // 5 minutes
const MAX_ENTRIES = 100;

// Map<string, { lastReportedAt: number, suppressedCount: number }>
const reportState = new Map();

function sanitizeErrorMessage(message) {
  if (!message) return "unknown";
  // Strip content representations from ProseMirror validation errors.
  // Format: "Invalid content for node <nodeName>: <content that may contain manuscript text>"
  // Keep only the prefix up through the node name.
  return message.replace(/^(Invalid content for node \w+).*$/, "$1");
}

function getErrorSignature(error) {
  return `${error?.name || "Error"}:${sanitizeErrorMessage(error?.message)}`;
}

function getKey(documentId, error) {
  return `${documentId}:${getErrorSignature(error)}`;
}

export function reportContentError(error, documentId) {
  const key = getKey(documentId, error);
  const now = Date.now();
  const entry = reportState.get(key);

  if (entry) {
    const elapsed = now - entry.lastReportedAt;
    if (elapsed < SUPPRESSION_INTERVAL_MS) {
      entry.suppressedCount += 1;
      return;
    }
    // Interval expired — report with suppressed count
    const suppressedCount = entry.suppressedCount;
    entry.lastReportedAt = now;
    entry.suppressedCount = 0;
    Sentry.captureException(error, {
      tags: { documentId },
      extra: { suppressedCount },
    });
    return;
  }

  // First occurrence — report immediately
  if (reportState.size >= MAX_ENTRIES) {
    const oldestKey = reportState.keys().next().value;
    reportState.delete(oldestKey);
  }
  reportState.set(key, { lastReportedAt: now, suppressedCount: 0 });
  Sentry.captureException(error, {
    tags: { documentId },
  });
}

export function resetContentErrorReporting() {
  reportState.clear();
}
